import React, { useState, useEffect, useRef, useLayoutEffect, useContext } from 'react'
import i18n from 'live/services/i18n'
import LiveModal from 'live/components/ui/Modal'
import { useFormState } from 'react-use-form-state'
import _ from 'lodash'
import { Field } from 'plugin-liverig/shared/asset/components/Fieldset'
import CustomDateTimeInput from '../components/custom-dt-format'
import DatePicker from 'react-datepicker'
import dateService from 'live/services/date'
import { detailedErrorHandler } from 'plugin-liverig/shared/asset/lib/utils'
import moment from 'moment'
import {
    Container,
    FormConfig,
    StyledLiveModal,
    InputContainerDatepickerStyled,
    ContainerDate,
    ContentBody,
    OptionContent
} from '../styles/styles'
import { Checkbox } from 'react-bootstrap'
import {
    getInterventionTitle,
    getReprocessTimespan,
    isInterventionValid,
    getCauseForInvalidIntervention,
    triggerSync,
    getStats
} from '../functions/functions'
import ButtonWithLoading from 'live/components/forms/ui/button-with-loading'
import { DvDDataContext } from '../context/DvDDataContext'

type ReprocessModalFormProps = {
    setShow: Function
    wellId: string
    title: string
    containsUnsavedData: boolean
    value: {
        lastDataPointIndex: number
        intervention: Array<Record<string, unknown>>
        begin: number
        end: number
    }
}

export function ReprocessModalForm({ setShow, wellId, title, containsUnsavedData, value }: ReprocessModalFormProps) {
    const [initialValues, setInitialValues] = useState<Record<string, unknown>>()
    const refActiveIntervention = useRef<Record<string, unknown>>(null)
    const refActiveInterventionSpan = useRef<Record<string, unknown>>({ begin: 0, end: 0 })
    const refSelectedInterventions = useRef<Array<Record<string, unknown>>>(null)
    const [sourceType, setSourceType] = useState<boolean>()
    const inputRef = useRef(null)
    const [defaultSpanType, setDefaultSpanType] = useState('')
    const [canReprocess, setCanReprocess] = useState<boolean>(false)
    const [datasetStats, setDatasetStats] = useState<Record<string, unknown>>()

    const {
        setUnSavedData,
        setLoadingDataset,
        setlastNewDataSyncTime,
        setlastDataEditTime,
        setWellSyncStatus,
        isSyncForced,
        setIsSyncForced,
        setTableData,
        loadingDataset,
        tableData
    } = useContext(DvDDataContext)

    const [formState, { label, raw }] = useFormState(initialValues, { withIds: true })

    const valuesToData = formValues => ({
        ...(value || {}),
        begin: formValues.begin || value.begin,
        end: formValues.end || value.end
    })

    useEffect(() => {
        setInitialValues({
            begin: value.begin,
            end: value.end,
            clear: false
        })
    }, [value])

    useEffect(() => {
        if (value && value.intervention && value.intervention.length) {
            const filteredInterventions = value.intervention.filter(i => i?.status === 'active')
            if (filteredInterventions && filteredInterventions.length > 0) {
                refActiveIntervention.current = filteredInterventions[0]
                refActiveInterventionSpan.current = {}
            }
        }

        if (!refActiveIntervention.current) {
            //setDefaultSpanType('All Interventions')
        } else {
            const x = { begin: 0, end: 0 }
            if (value.lastDataPointIndex > 0) {
                x.begin = value.lastDataPointIndex
            } else {
                x.begin = refActiveIntervention.current.begin
            }

            x.end = Date.now()
            if (Date.now() > refActiveIntervention.current.end) {
                x.end = refActiveIntervention.current.end
            }
            refActiveInterventionSpan.current = x
        }
    }, [value])

    useEffect(() => {
        formState.clear()
        for (const field in initialValues) {
            setField(field, initialValues[field])
        }
        setField('showComment', false)
        setCanReprocess(false)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [initialValues])

    const closeModal = () => setShow(false)

    const parseDate = React.useCallback(date => {
        if (!date) return null
        if (moment.isMoment(date)) return +date._d

        const parsed = parseInt(date)

        if (isNaN(parsed)) return null

        return parsed
    }, [])

    useLayoutEffect(() => {
        if (inputRef.current) inputRef.current.focus()
    }, [inputRef])

    async function _onSubmit() {
        await triggerSync(
            wellId,
            formState.values.begin,
            formState.values.end,
            formState.values.clear,
            'ds_day_depth',
            setLoadingDataset,
            setIsSyncForced,
            setTableData,
            setWellSyncStatus,
            setUnSavedData,
            setlastDataEditTime,
            setlastNewDataSyncTime,
            isSyncForced,
            setShow
        )
    }

    const setField = (key, value) => {
        formState.clearField(key)
        formState.setField(key, value)
    }

    const onChangeSpanSelector = span => {
        setCanReprocess(false)
        if (span === 'Active Intervention') {
            setDefaultSpanType(span)
            setField('end', refActiveInterventionSpan.current.end)
            setField('begin', refActiveInterventionSpan.current.begin)
            setField('clear', false)
            setField('showComment', true)
        } else {
            setDefaultSpanType(span)
            setField('begin', 0)
            setField('end', 0)
            setField('showComment', false)
            if (span === 'Custom Time Range') {
                setField('clear', false)
            } else {
                setField('clear', true)
            }
        }
    }

    const onChangeInterventionSelector = (event, intervention) => {
        if (
            !(
                refSelectedInterventions &&
                refSelectedInterventions.current &&
                refSelectedInterventions.current.length > 0
            )
        ) {
            refSelectedInterventions.current = []
        }

        if (event.currentTarget.checked) {
            refSelectedInterventions.current.push(intervention)
        } else {
            refSelectedInterventions.current = refSelectedInterventions.current.filter(x => x.id !== intervention.id)
        }

        if (refSelectedInterventions.current.length > 0) {
            let b = Number.MAX_VALUE
            let e = Number.MIN_VALUE

            for (let i = 0; i < refSelectedInterventions.current.length; i++) {
                if (refSelectedInterventions.current[i].begin < b) {
                    b = refSelectedInterventions.current[i].begin
                }

                if (refSelectedInterventions.current[i].end > e) {
                    e = refSelectedInterventions.current[i].end
                }
            }
            setField('begin', b)
            setField('end', e)
            setField('showComment', true)
        } else {
            setField('showComment', false)
            setField('begin', 0)
            setField('end', 0)
        }
    }

    const onChangeDatePicker = (date, type) => {
        const dt = parseDate(date)
        if (type === 'begin') {
            setField('begin', dt)
            setField('end', null)
        } else if (type === 'end') {
            setField('end', dt)
        }
        setField('showComment', true)
    }

    return (
        <StyledLiveModal className="modal-lg" onHide={closeModal} show={true}>
            <LiveModal.Header>
                <LiveModal.Title>{title ? title : i18n('Reprocess Data')}</LiveModal.Title>
            </LiveModal.Header>
            <LiveModal.Body>
                <FormConfig>
                    <ContentBody>
                        <React.Fragment>
                            <Container>
                                <section>
                                    <header>{i18n('Timespan Options')}</header>
                                    <div className="form-group radio-box">
                                        <label className={`radio-inline`}>
                                            <input
                                                type="radio"
                                                checked={defaultSpanType === 'Active Intervention'}
                                                onChange={() => onChangeSpanSelector('Active Intervention')}
                                                disabled={!refActiveIntervention.current}
                                            />
                                            &nbsp;
                                            {i18n('Active Intervention')}
                                        </label>
                                        <label className={`radio-inline`}>
                                            <input
                                                type="radio"
                                                checked={defaultSpanType === 'Custom Time Range'}
                                                onChange={() => onChangeSpanSelector('Custom Time Range')}
                                                // disabled={!enableChecking}
                                            />
                                            &nbsp;
                                            {i18n('Custom Time Range')}
                                        </label>
                                        <label className={`radio-inline`}>
                                            <input
                                                type="radio"
                                                checked={defaultSpanType === 'All Interventions'}
                                                onChange={() => onChangeSpanSelector('All Interventions')}
                                                disabled={!(value.intervention.length > 0)}
                                            />
                                            &nbsp;
                                            {i18n('All Interventions')}
                                        </label>
                                    </div>
                                </section>
                                <section style={{ height: '400px', overflow: 'scroll' }}>
                                    {defaultSpanType === 'Active Intervention' && (
                                        <div className="form-group">
                                            <header>
                                                {i18n('Active Intervention')}
                                                <subtitle>
                                                    {i18n(
                                                        `"Reprocess" will be performed using active intervention span and existing data.`
                                                    )}
                                                    <span style={{ color: 'red' }}>
                                                        &nbsp;
                                                        {containsUnsavedData ? `and your unsaved changes` : ``}
                                                    </span>
                                                </subtitle>
                                            </header>
                                        </div>
                                    )}
                                    {defaultSpanType === 'Custom Time Range' && (
                                        <div className="form-group">
                                            <header>
                                                {i18n('Custom Time Range')}
                                                <subtitle>
                                                    {i18n(`"Reprocess" will be performed between the span selected.`)}
                                                    <span style={{ color: 'red' }}>
                                                        &nbsp; This will clear existing data in the selected time range
                                                        {containsUnsavedData ? ` and your unsaved changes` : ``}
                                                    </span>
                                                </subtitle>
                                            </header>
                                            <ContainerDate>
                                                <Field>
                                                    <label {...label('begin')}>
                                                        <span>{i18n('Start date')}</span>
                                                    </label>
                                                    <InputContainerDatepickerStyled>
                                                        <DatePicker
                                                            maxDate={
                                                                formState.values.end
                                                                    ? moment(formState.values.end)
                                                                    : undefined
                                                            }
                                                            selected={
                                                                formState.values.begin
                                                                    ? moment(formState.values.begin)
                                                                    : undefined
                                                            }
                                                            customInput={<CustomDateTimeInput />}
                                                            dateFormat={dateService.getDateTimeFormat()}
                                                            placeholderText={dateService
                                                                .getDateTimeFormat()
                                                                .toLowerCase()}
                                                            fixedHeight
                                                            {...raw({
                                                                name: 'begin',
                                                                onChange: mom => {
                                                                    onChangeDatePicker(mom, 'begin')

                                                                    return parseDate(mom)
                                                                }
                                                            })}
                                                            showTimeSelect
                                                            yearDropdownItemNumber={5}
                                                        />
                                                    </InputContainerDatepickerStyled>
                                                </Field>
                                                <Field>
                                                    <label {...label('end')}>
                                                        <span>{i18n('End date')}</span>
                                                    </label>
                                                    <InputContainerDatepickerStyled>
                                                        <DatePicker
                                                            minDate={
                                                                formState.values.begin
                                                                    ? moment(formState.values.begin)
                                                                    : undefined
                                                            }
                                                            disabled={formState.values.begin <= 0}
                                                            customInput={<CustomDateTimeInput />}
                                                            selected={
                                                                formState.values.end
                                                                    ? moment(formState.values.end)
                                                                    : undefined
                                                            }
                                                            {...raw({
                                                                name: 'end',
                                                                onChange: mom => {
                                                                    onChangeDatePicker(mom, 'end')
                                                                    return parseDate(mom)
                                                                }
                                                            })}
                                                            dateFormat={dateService.getDateTimeFormat()}
                                                            placeholderText={dateService
                                                                .getDateTimeFormat()
                                                                .toLowerCase()}
                                                            fixedHeight
                                                            showTimeSelect
                                                            yearDropdownItemNumber={5}
                                                        />
                                                    </InputContainerDatepickerStyled>
                                                </Field>
                                            </ContainerDate>
                                        </div>
                                    )}
                                    {defaultSpanType === 'All Interventions' && value && value.intervention && (
                                        <div>
                                            <header>
                                                {i18n('All Interventions')}
                                                <subtitle>
                                                    {i18n(`"Reprocess" will be performed on the chosen interventions.`)}
                                                    <span style={{ color: 'red' }}>
                                                        &nbsp; This will clear all existing data
                                                        {containsUnsavedData ? ` and your unsaved changes` : ``}
                                                    </span>
                                                </subtitle>
                                            </header>
                                            <div style={{ paddingBottom: '10px' }}>
                                                {value.intervention.map(x => {
                                                    if (x.status == 'active' || x.status == 'finished') {
                                                        return (
                                                            <React.Fragment key={'A'}>
                                                                <OptionContent>
                                                                    <Checkbox
                                                                        type="checkbox"
                                                                        name="description"
                                                                        // checked={checkedItens[element.key] && !element.disabled}
                                                                        onChange={event =>
                                                                            onChangeInterventionSelector(event, x)
                                                                        }
                                                                        key={'A'}
                                                                        disabled={!isInterventionValid(x)}
                                                                    >
                                                                        {getInterventionTitle(x)}
                                                                        {/* {!isInterventionValid(x) && (
                                                                        <div style={{ color: 'red' }}>
                                                                            {getCauseForInvalidIntervention(x)}
                                                                        </div>
                                                                    )} */}
                                                                    </Checkbox>
                                                                </OptionContent>
                                                            </React.Fragment>
                                                        )
                                                    }
                                                })}
                                            </div>
                                        </div>
                                    )}
                                    <div className="form-group">
                                        <note>
                                            {getReprocessTimespan(
                                                formState.values.begin,
                                                formState.values.end,
                                                formState.values.showComment,
                                                defaultSpanType,
                                                tableData
                                            )}
                                        </note>
                                    </div>
                                </section>
                            </Container>
                        </React.Fragment>
                    </ContentBody>
                </FormConfig>
            </LiveModal.Body>
            <LiveModal.Footer>
                <ButtonWithLoading
                    className={`btn accept btn- mr-5`}
                    loading={loadingDataset || isSyncForced}
                    buttonMessage={'Reprocess'}
                    buttonMessageLoading={'Reprocessing'}
                    onClick={_onSubmit}
                    disabled={!(formState.values.begin > 0 && formState.values.end > formState.values.begin)}
                />

                {!loadingDataset && !isSyncForced ? (
                    <span>
                        <span>{i18n('or')}</span>
                        <a
                            className="cancel"
                            onClick={() => {
                                setShow(false)
                            }}
                        >
                            {' '}
                            {i18n('cancel')}
                        </a>
                    </span>
                ) : null}
            </LiveModal.Footer>
        </StyledLiveModal>
    )
}

function valid({ start, end, lastDataPointIndex }: { start: number; end: number; lastDataPointIndex: number }) {
    return !!start && !!end && !!lastDataPointIndex
}
